import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SEO_CITIES, SEO_COUNTRIES, cityUrl } from "@/data/seoCities";

// Maillage interne intelligent — liens crawlables entre annonces similaires,
// recherches thématiques et pages de villes, pour que Google indexe le site
// comme un réseau cohérent (inspiration AutoSEO).
export default function SeoInternalLinks({ car }) {
  const links = useMemo(() => {
    if (!car) return [];
    const out = [];
    const enc = encodeURIComponent;
    if (car.make) out.push({ label: `Toutes les ${car.make}`, to: `/search?make=${enc(car.make)}` });
    if (car.make && car.model) out.push({ label: `${car.make} ${car.model} d'occasion`, to: `/search?make=${enc(car.make)}&model=${enc(car.model)}` });
    if (car.body_type) out.push({ label: `${car.body_type} d'occasion`, to: `/search?bodyType=${enc(car.body_type)}` });
    if (car.fuel) out.push({ label: `${car.make || "Véhicules"} ${car.fuel}`, to: `/search?make=${enc(car.make || "")}&fuel=${enc(car.fuel)}` });
    if (car.year) out.push({ label: `${car.make || "Véhicules"} à partir de ${car.year}`, to: `/search?make=${enc(car.make || "")}&minYear=${car.year}` });
    return out;
  }, [car]);

  const cities = useMemo(() => {
    if (!car) return { country: null, list: [] };
    const cc = String(car.country_code || "be").toLowerCase();
    const co = SEO_COUNTRIES.find((c) => c.code === cc) || SEO_COUNTRIES[0];
    const all = SEO_CITIES[co.code] || [];
    const locName = String(car.location || "").toLowerCase();
    const sorted = [...all].sort((a, b) => {
      const am = locName.includes(a.name.toLowerCase()) ? 0 : 1;
      const bm = locName.includes(b.name.toLowerCase()) ? 0 : 1;
      return am - bm;
    });
    return { country: co, list: sorted.slice(0, 8) };
  }, [car]);

  if (!links.length && !cities.list.length) return null;

  return (
    <section className="mt-14 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="seo-internal-links">
      <h2 className="font-display text-lg font-extrabold text-white sm:text-xl">Explorez davantage sur Pactly Car</h2>

      {links.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="seo-thematic-links">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid="seo-thematic-link"
              className="group inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-xs font-semibold text-white/80 transition hover:border-[#6EE7B7]/40 hover:text-emerald-200">
              {l.label}
              <ArrowUpRight size={13} className="opacity-60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          ))}
        </div>
      )}

      {cities.list.length > 0 && cities.country && (
        <div className="mt-5" data-testid="seo-city-links">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/45">
            <MapPin size={12} /> {cities.country.flag} Voitures d'occasion par ville
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {cities.list.map((c) => (
              <Link key={c.slug} to={cityUrl(cities.country.code, c.slug)} data-testid="seo-city-link"
                className="text-xs font-medium text-white/55 underline-offset-2 transition hover:text-white hover:underline">
                {cities.country.term === "voitures" ? "Voitures" : "Autos"} à {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
